'use client';

import {
  Type,
  AlignLeft,
  Hash,
  Calendar,
  List,
  CheckSquare,
  Phone,
  AtSign
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import type { LocalField, DynamicFormField, DynamicFieldType } from '../types';

type FieldLike = LocalField | DynamicFormField;

interface FormFieldRendererProps {
  field: FieldLike;
}

const FIELD_TYPE_ICONS: Record<
  DynamicFieldType,
  React.ComponentType<{ className?: string }>
> = {
  short_text: Type,
  long_text: AlignLeft,
  number: Hash,
  date: Calendar,
  selection_list: List,
  yes_no: CheckSquare,
  phone: Phone,
  email: AtSign
};

export function FormFieldRenderer({ field }: FormFieldRendererProps) {
  const Icon = FIELD_TYPE_ICONS[field.type];

  const label = (
    <div className='mb-2 flex items-center justify-end gap-1'>
      <Icon className='text-muted-foreground h-4 w-4' />
      <span className='text-sm font-medium'>{field.label}</span>
      {field.is_required && <span className='text-red-500'>*</span>}
    </div>
  );

  switch (field.type) {
    case 'short_text':
      return (
        <div>
          {label}
          <Input disabled placeholder={field.label} />
        </div>
      );
    case 'long_text':
      return (
        <div>
          {label}
          <Textarea disabled placeholder={field.label} rows={3} />
        </div>
      );
    case 'number':
      return (
        <div>
          {label}
          <Input disabled type='number' placeholder='0' />
        </div>
      );
    case 'phone':
      return (
        <div>
          {label}
          <Input disabled type='tel' placeholder='+000 000 0000' />
        </div>
      );
    case 'email':
      return (
        <div>
          {label}
          <Input disabled type='email' placeholder='email@example.com' />
        </div>
      );
    case 'date':
      return (
        <div>
          {label}
          <Input disabled type='date' />
        </div>
      );
    case 'selection_list': {
      const options = (field.options ?? []).filter(
        (o) => typeof o === 'string' && o.trim()
      );
      if ('is_multi_select' in field && field.is_multi_select) {
        return (
          <div>
            {label}
            <div className='space-y-2'>
              {options.map((option, i) => (
                <div key={i} className='flex items-center gap-2'>
                  <Checkbox disabled />
                  <span className='text-sm'>{option}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div>
          {label}
          <Select disabled defaultValue={field.default_value || undefined}>
            <SelectTrigger>
              <SelectValue placeholder={field.label} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option, i) => (
                <SelectItem key={i} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    case 'yes_no':
      return (
        <div className='flex items-center justify-between rounded-lg border p-4'>
          <div className='flex items-center gap-1'>
            <Icon className='text-muted-foreground h-4 w-4' />
            <span className='text-sm font-medium'>{field.label}</span>
            {field.is_required && <span className='text-red-500'>*</span>}
          </div>
          <Switch disabled />
        </div>
      );
    default:
      return null;
  }
}
